import axios from 'axios'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

export interface TemplateData {
  userName?: string
  userEmail?: string
  testTitle?: string
  dueDate?: Date
  score?: number
  passed?: boolean
  daysLeft?: number
  temporaryPassword?: string
  newPassword?: string
  resetUrl?: string
  companyName?: string
}

export interface EmailTemplate {
  id: string
  templateKey: string
  name: string
  description: string | null
  subject: string
  htmlBody: string
  textBody: string | null
  variables: string[]
  category: string
  isActive: boolean
  isSystem: boolean
  createdAt: string
  updatedAt: string
}

export interface EmailTemplateListItem {
  templateKey: string
  name: string
  description: string | null
  category: string
  variables: string[]
  isActive: boolean
  isSystem: boolean
  updatedAt: string
}

export interface EmailTemplatesResponse {
  templates: EmailTemplateListItem[]
  groupedByCategory: Record<string, EmailTemplateListItem[]>
  total: number
  message: string
}

export interface EmailPreview {
  templateKey: string
  subject: string
  html: string
  text?: string
  sampleData: TemplateData
}

export interface SendEmailRequest {
  to: string
  subject: string
  text?: string
  html?: string
  templateId?: string
  dynamicTemplateData?: Record<string, any>
}

export interface BulkEmailRequest {
  to: string[]
  subject: string
  text?: string
  html?: string
  templateId?: string
  dynamicTemplateData?: Record<string, any>
}

export interface TemplateEmailRequest {
  to: string
  templateName: string
  templateData: TemplateData
}

class EmailService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken')
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  /**
   * 基本メール送信
   */
  async sendEmail(emailData: SendEmailRequest) {
    const response = await axios.post(`${API_BASE_URL}/email/send`, emailData, {
      headers: this.getAuthHeaders(),
    })
    return response.data
  }

  /**
   * 一括メール送信
   */
  async sendBulkEmail(emailData: BulkEmailRequest) {
    const response = await axios.post(`${API_BASE_URL}/email/send-bulk`, emailData, {
      headers: this.getAuthHeaders(),
    })
    return response.data
  }

  /**
   * テンプレートベースメール送信
   */
  async sendTemplateEmail(emailData: TemplateEmailRequest) {
    const response = await axios.post(`${API_BASE_URL}/email/templates/send`, emailData, {
      headers: this.getAuthHeaders(),
    })
    return response.data
  }

  /**
   * 受講開始通知送信
   */
  async sendEnrollmentNotification(enrollmentId: string) {
    const response = await axios.post(
      `${API_BASE_URL}/email/enrollment-notification`,
      { enrollmentId },
      { headers: this.getAuthHeaders() }
    )
    return response.data
  }

  /**
   * 期限リマインダー一括送信
   */
  async sendDeadlineReminders(daysBeforeDeadline = 3) {
    const response = await axios.post(
      `${API_BASE_URL}/email/deadline-reminders`,
      { daysBeforeDeadline },
      { headers: this.getAuthHeaders() }
    )
    return response.data
  }

  /**
   * メール設定確認
   */
  async checkEmailConfiguration() {
    const response = await axios.get(`${API_BASE_URL}/email/config`, {
      headers: this.getAuthHeaders(),
    })
    return response.data
  }

  /**
   * テストメール送信
   */
  async sendTestEmail(to: string) {
    const response = await axios.post(
      `${API_BASE_URL}/email/test`,
      { to },
      { headers: this.getAuthHeaders() }
    )
    return response.data
  }

  /**
   * テンプレート一覧取得
   */
  async getTemplates(): Promise<EmailTemplatesResponse> {
    const response = await axios.get(`${API_BASE_URL}/email/templates`, {
      headers: this.getAuthHeaders(),
    })
    return response.data
  }

  /**
   * テンプレートプレビュー生成
   */
  async previewTemplate(templateKey: string, templateData: TemplateData): Promise<EmailPreview> {
    const response = await axios.post(
      `${API_BASE_URL}/email/templates/${templateKey}/preview`,
      templateData,
      { headers: this.getAuthHeaders() }
    )
    return response.data
  }

  /**
   * テンプレート詳細取得
   */
  async getTemplateDetails(templateKey: string): Promise<EmailTemplate> {
    const response = await axios.get(`${API_BASE_URL}/email/templates/${templateKey}`, {
      headers: this.getAuthHeaders(),
    })
    return response.data
  }

  /**
   * テンプレート保存/更新
   */
  async saveTemplate(
    templateKey: string,
    templateData: {
      name?: string
      description?: string
      subject?: string
      htmlBody?: string
      textBody?: string
      category?: string
      isActive?: boolean
    }
  ) {
    const response = await axios.put(
      `${API_BASE_URL}/email/templates/${templateKey}`,
      templateData,
      { headers: this.getAuthHeaders() }
    )
    return response.data
  }

  /**
   * カスタムテンプレート削除（デフォルトに戻す）
   * @deprecated システムテンプレートは削除できません
   */
  async deleteCustomTemplate(templateKey: string) {
    const response = await axios.delete(`${API_BASE_URL}/email/templates/${templateKey}`, {
      headers: this.getAuthHeaders(),
    })
    return response.data
  }

  /**
   * HTMLエディター用のサンプル変数データ生成
   */
  generateSampleData(): TemplateData {
    return {
      userName: 'テストユーザー',
      testTitle: 'サンプルコンプライアンステスト',
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7日後
      score: 85,
      passed: true,
      daysLeft: 3,
      resetUrl: 'https://example.com/reset-password?token=sample',
      companyName: 'サンプル株式会社',
    }
  }

  /**
   * HTMLテンプレートの変数プレースホルダー置換
   */
  replaceVariables(htmlTemplate: string, data: TemplateData): string {
    let result = htmlTemplate

    Object.entries(data).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g')
      let replaceValue = ''

      if (value instanceof Date) {
        replaceValue = value.toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
        })
      } else if (typeof value === 'boolean') {
        replaceValue = value ? '合格' : '不合格'
      } else {
        replaceValue = String(value || '')
      }

      result = result.replace(regex, replaceValue)
    })

    return result
  }
}

export const emailService = new EmailService()
export default emailService
